import {
  BASE_SAMPLES_PER_BUCKET,
  WAVEFORM_VERSION,
  type WaveformLevel,
  type WaveformPeaks,
} from './waveformTypes';

/**
 * Waveform peak computation.
 *
 * The naive approach — draw every sample — is unusable: a four-minute stereo
 * file at 48kHz is 23 million samples per channel, and a timeline redraw at
 * 60fps would have to touch all of them. Instead we build a mip pyramid of
 * min/max pairs once, at import time, and cache it. Drawing then reads roughly
 * one bucket per screen pixel at any zoom level, so redraw cost is bounded by
 * the width of the window rather than by the length of the audio.
 *
 * Level 0 buckets 256 samples (~5ms at 48kHz, finer than any pixel at usable
 * zooms); each further level halves resolution until a level fits in a few
 * hundred buckets. Total storage is under 2x level 0, i.e. about 0.8% of the
 * source audio.
 */

export function computePeaks(
  assetId: string,
  channels: readonly Float32Array[],
  sampleRate: number,
  durationSec: number,
): WaveformPeaks {
  const channelCount = Math.max(1, channels.length);
  const sampleCount = channels[0]?.length ?? 0;

  const levels: WaveformLevel[] = [];
  let samplesPerBucket = BASE_SAMPLES_PER_BUCKET;
  let previous: WaveformLevel | null = null;

  // Build levels until one is small enough to draw whole, with a hard cap so a
  // pathological input cannot spin here.
  for (let levelIndex = 0; levelIndex < 16; levelIndex++) {
    const bucketCount = Math.max(1, Math.ceil(sampleCount / samplesPerBucket));
    const level: WaveformLevel = {
      samplesPerBucket,
      bucketCount,
      channels: [],
    };

    for (let c = 0; c < channelCount; c++) {
      const out = new Int8Array(bucketCount * 2);
      if (previous === null) {
        const data = channels[c] ?? channels[0];
        for (let b = 0; b < bucketCount; b++) {
          const start = b * samplesPerBucket;
          const end = Math.min(sampleCount, start + samplesPerBucket);
          let min = 0;
          let max = 0;
          for (let i = start; i < end; i++) {
            const v = data[i];
            if (v < min) min = v;
            if (v > max) max = v;
          }
          out[b * 2] = quantise(min);
          out[b * 2 + 1] = quantise(max);
        }
      } else {
        // Higher levels are built from the level below, not from the source:
        // O(n) once rather than O(n) per level.
        const src = previous.channels[c];
        for (let b = 0; b < bucketCount; b++) {
          const i0 = b * 2 * 2;
          const i1 = i0 + 2;
          const min = Math.min(src[i0] ?? 0, i1 < src.length ? src[i1] : 0);
          const max = Math.max(src[i0 + 1] ?? 0, i1 + 1 < src.length ? src[i1 + 1] : 0);
          out[b * 2] = min;
          out[b * 2 + 1] = max;
        }
      }
      level.channels.push(out);
    }

    levels.push(level);
    previous = level;
    if (bucketCount <= 512) break;
    samplesPerBucket *= 2;
  }

  return {
    version: WAVEFORM_VERSION,
    assetId,
    sampleRate,
    channelCount,
    durationSec,
    levels,
  };
}

function quantise(value: number): number {
  const clamped = value < -1 ? -1 : value > 1 ? 1 : value;
  return Math.round(clamped * 127);
}

/** Int8 peak back to -1..1. */
export function dequantise(value: number): number {
  return value / 127;
}

/**
 * Reads a min/max envelope for a pixel column range, resampling from whichever
 * mip level is closest to the requested density. `out` is filled with
 * [min, max] pairs and returned, so callers can reuse one array per redraw.
 */
export function readEnvelope(
  peaks: WaveformPeaks,
  channel: number,
  startSample: number,
  samplesPerPixel: number,
  pixelCount: number,
  out: Float32Array,
): Float32Array {
  const level = peaks.levels.reduce((best, current) =>
    current.samplesPerBucket <= samplesPerPixel ? current : best,
  );
  const data = level.channels[Math.min(channel, level.channels.length - 1)];
  const bucketsPerPixel = samplesPerPixel / level.samplesPerBucket;
  const startBucket = startSample / level.samplesPerBucket;

  for (let p = 0; p < pixelCount; p++) {
    const from = Math.floor(startBucket + p * bucketsPerPixel);
    const to = Math.max(from + 1, Math.ceil(startBucket + (p + 1) * bucketsPerPixel));
    let min = 0;
    let max = 0;
    for (let b = from; b < to; b++) {
      if (b < 0 || b >= level.bucketCount) continue;
      const lo = data[b * 2];
      const hi = data[b * 2 + 1];
      if (lo < min) min = lo;
      if (hi > max) max = hi;
    }
    out[p * 2] = dequantise(min);
    out[p * 2 + 1] = dequantise(max);
  }
  return out;
}

/**
 * The same envelope for columns of *uneven* width in the source.
 *
 * `edges` holds `pixelCount + 1` sample positions, the boundaries between
 * columns; a column covers whatever lies between its two edges, in either
 * order, so a clip read backwards is a descending list and needs nothing else.
 * This is what draws a warped clip the way it is heard: the columns are placed
 * by the warp curve and the reader simply takes what falls in each.
 */
export function readEnvelopeBetween(
  peaks: WaveformPeaks,
  channel: number,
  edges: ArrayLike<number>,
  pixelCount: number,
  out: Float32Array,
): Float32Array {
  const meanSamplesPerPixel = Math.max(1, Math.abs(edges[pixelCount] - edges[0]) / Math.max(1, pixelCount));
  const level = peaks.levels.reduce((best, current) =>
    current.samplesPerBucket <= meanSamplesPerPixel ? current : best,
  );
  const data = level.channels[Math.min(channel, level.channels.length - 1)];

  for (let p = 0; p < pixelCount; p++) {
    const a = edges[p] / level.samplesPerBucket;
    const b = edges[p + 1] / level.samplesPerBucket;
    const from = Math.floor(Math.min(a, b));
    const to = Math.max(from + 1, Math.ceil(Math.max(a, b)));
    let min = 0;
    let max = 0;
    for (let bucket = from; bucket < to; bucket++) {
      if (bucket < 0 || bucket >= level.bucketCount) continue;
      const lo = data[bucket * 2];
      const hi = data[bucket * 2 + 1];
      if (lo < min) min = lo;
      if (hi > max) max = hi;
    }
    out[p * 2] = dequantise(min);
    out[p * 2 + 1] = dequantise(max);
  }
  return out;
}

/** Total bytes held by a peak set, used by the cache budget. */
export function peaksByteLength(peaks: WaveformPeaks): number {
  let total = 0;
  for (const level of peaks.levels) for (const channel of level.channels) total += channel.byteLength;
  return total;
}
